import { ElementType, memo, useCallback, useState } from 'react';
import { MESSAGES } from 'src/modules/common/constants';
import { getExcelFileName } from 'src/utils/getExcelFileName';
import { DownloadIcon } from 'src/modules/common/icons/DownloadIcon';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { Button, ButtonProps } from 'src/common/Button/Button';
import { UserSettings } from 'src/types/UserSettings';
import { SelectionRepository } from 'src/projects/SelectionRepository';
import { saveAs } from 'file-saver';
import { composeExcelBuffer } from 'src/projects/hooks/composeExcelBuffer';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate';
import { exportAllPrepare } from 'src/projects/hooks/exportAllPrepare';

type Props = {
    selectionRepository: SelectionRepository;
    userSettings: UserSettings;
    disabled?: boolean;
    kind?: ButtonProps<ElementType>['kind'];
    size?: ButtonProps<ElementType>['size'];
}

export const ExportExcelButton = memo(({
    selectionRepository,
    userSettings,
    disabled,
    kind = 'ghost-secondary',
    size = 'md',
}: Props) => {
    const [inProgress, setInProgress] = useState<boolean>(false);
    const t = useBelimoTranslate();
    const onClick = useCallback(async () => {
        if (inProgress || disabled) {
            return;
        }
        setInProgress(true);
        const data = await exportAllPrepare({
            selectionRepository,
            userSettings,
            t,
        });
        const buffer = await composeExcelBuffer(data);
        saveAs(new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }), getExcelFileName());
        setInProgress(false);
    }, [inProgress, disabled, selectionRepository, userSettings, t]);
    return (
        <Button
            type="button"
            kind={kind}
            size={size}
            onClick={onClick}
            loading={inProgress}
            disabled={disabled}
            icon={(
                <div className="vss-project-page__icon">
                    <DownloadIcon/>
                </div>
            )}
        >
            <span className="vss-project-page__btn-label">
                <I18n messageId={MESSAGES.projectPageDownloadExcel}/>
            </span>
        </Button>
    );
});
